import {MAKE_TEXT_EDITABLE_POLL_TIMEOUT_MS, pollMakeTextEditableLayersStatus} from '@Components/ai-chat-window/lib/make-text-editable.library';
import {getMakeTextEditableLoadingTexts, getMakeTextEditableLoadingTitle} from '@Components/ai-chat-window/lib/make-text-editable-layers-visual';
import {getUrlWithLayeringFeedbackParam, showLayeredPosterFeedbackToast} from '@Components/ai-chat-window/lib/make-text-editable-feedback.library';
import {runPendingDesignReveal} from '@Components/poster-editor/library/pending-design-reveal.library';
import type {PendingDesignReady} from '@Components/poster-editor/library/pending-design-reveal.library.types';
import {PendingDesignPollStatus} from '@Components/poster-editor/library/pending-design-reveal.library.types';
import {LayeringType} from '@Libraries/layering-type';
import {getEditPosterURL} from '@Libraries/poster-editor.library';

interface PendingLayersFlowOptions {
  originalImageUrl?: string;
  layeringType?: LayeringType;
}

/**
 * Editor-tab half of the desktop "Editable layers" flow, run through the poster-ui port when a loaded poster
 * carries a pending layers job id: the shared pending-design reveal with this job's poll, copy and feedback.
 * The job never reports a timeout as "still working" — a layers poll that runs out of budget is a failure.
 */
export const runPendingLayersFlow = (jobId: string, {originalImageUrl, layeringType = LayeringType.TEXT}: PendingLayersFlowOptions = {}): Promise<void> => {
  return runPendingDesignReveal({
    jobId,
    originalImageUrl: originalImageUrl ?? null,
    loadingCopy: {title: getMakeTextEditableLoadingTitle(layeringType), texts: getMakeTextEditableLoadingTexts()},
    readyGrowlText: window.i18next.t('pmwjs_mte_design_can_now_be_edited'),
    failureGrowlText: window.i18next.t('pmwjs_something_went_wrong'),
    pollJob: pollLayersJob,
    showFeedback: (posterHashedId: string): void => {
      showLayeredPosterFeedbackToast(posterHashedId, layeringType);
    },
    getPosterUrlWithFeedback: (posterHashedId: string): string => {
      return getUrlWithLayeringFeedbackParam(getEditPosterURL(posterHashedId), layeringType);
    },
  });
};

const pollLayersJob = async (jobId: string): Promise<PendingDesignReady> => {
  const {posterHashedId} = await pollMakeTextEditableLayersStatus(jobId, MAKE_TEXT_EDITABLE_POLL_TIMEOUT_MS);
  return {status: PendingDesignPollStatus.READY, posterHashedId};
};
